import { useMemo, useState } from 'react';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import {
    ManageChatsTabValue,
    ManageChatItemVm,
    ProviderFilterValue,
    SortValue,
    UseManageChatsDialogOptions,
    UseManageChatsDialogVm,
} from './useChatList';

const matchesManageChatsProvider = (item: ManageChatItemVm, provider: ProviderFilterValue): boolean => {
    if (provider === 'all') return true;
    if (provider === 'progpt') return item.providerType === ChatProviderType.ProGPT;
    if (provider === 'nexus') return item.providerType === ChatProviderType.Nexus;
    return item.providerType === ChatProviderType.Assistants;
};

const sortManageChatItems = (items: ManageChatItemVm[], sortBy: SortValue): ManageChatItemVm[] => {
    const next = [...items];

    if (sortBy === 'title') {
        next.sort((left, right) => left.title.localeCompare(right.title));
        return next;
    }

    next.sort((left, right) =>
        sortBy === 'oldest' ? left.modifiedDate - right.modifiedDate : right.modifiedDate - left.modifiedDate,
    );
    return next;
};

export const useManageChatsDialog = ({
    items,
    selectedIds,
    setSelectedSessions,
    clearSelection,
}: UseManageChatsDialogOptions): UseManageChatsDialogVm => {
    const [selectedTab, setSelectedTab] = useState<ManageChatsTabValue>('active');
    const [searchQuery, setSearchQuery] = useState('');
    const [providerFilter, setProviderFilter] = useState<ProviderFilterValue>('all');
    const [agentFilter, setAgentFilter] = useState('all');
    const [sortBy, setSortBy] = useState<SortValue>('recent');

    const agentOptions = useMemo(() => {
        const entries = new Map<string, string>();

        for (const item of items) {
            if (item.agentId && item.providerType === ChatProviderType.Nexus) {
                entries.set(item.agentId, item.agentName);
            }
        }

        return [...entries.entries()].sort((left, right) => left[1].localeCompare(right[1]));
    }, [items]);

    const filteredItems = useMemo(() => {
        const normalizedQuery = searchQuery.trim().toLowerCase();

        const filtered = items.filter((item) => {
            const matchesTab = selectedTab === 'archived' ? item.isArchived : !item.isArchived;
            const matchesQuery = normalizedQuery.length === 0 || item.title.toLowerCase().includes(normalizedQuery);
            const matchesAgent = selectedTab === 'archived' || agentFilter === 'all' || item.agentId === agentFilter;
            const matchesProvider = selectedTab === 'archived' || matchesManageChatsProvider(item, providerFilter);

            return matchesTab && matchesQuery && matchesProvider && matchesAgent;
        });

        return sortManageChatItems(filtered, sortBy);
    }, [agentFilter, items, providerFilter, searchQuery, selectedTab, sortBy]);

    const visibleSelectableIds = useMemo(() => {
        return filteredItems.map((item) => item.sessionId);
    }, [filteredItems]);

    const selectedVisibleCount = useMemo(() => {
        return visibleSelectableIds.filter((id) => selectedIds.has(id)).length;
    }, [selectedIds, visibleSelectableIds]);

    const allVisibleSelected = visibleSelectableIds.length > 0 && selectedVisibleCount === visibleSelectableIds.length;

    const headerCheckboxState: boolean | 'mixed' =
        visibleSelectableIds.length === 0
            ? false
            : allVisibleSelected
              ? true
              : selectedVisibleCount > 0
                ? 'mixed'
                : false;

    const handleHeaderCheckboxChange = (checked: boolean): void => {
        if (checked) {
            setSelectedSessions(visibleSelectableIds);
            return;
        }

        clearSelection();
    };

    return {
        selectedTab,
        setSelectedTab,
        searchQuery,
        setSearchQuery,
        providerFilter,
        setProviderFilter,
        agentFilter,
        setAgentFilter,
        sortBy,
        setSortBy,
        agentOptions,
        filteredItems,
        visibleSelectableIds,
        headerCheckboxState,
        handleHeaderCheckboxChange,
    };
};

export default useManageChatsDialog;
